import * as React from "react";
import { Typography, Box, Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { NavMenuOption, PageLayout } from "components/PageLayout/PageLayout";
import { BreadcrumbItem } from "components/Breadcrumbs/Breadcrumbs";
import { LqaRatingReportsList } from "./LqaRatingReportsList";
import { useMutation, useQueryClient } from "react-query";
import { MetricsService } from "gen/clients";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { Form, Formik } from "formik";
import { default as dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { DATE_PICKER_FORMAT, DatePickerField } from "components/formikFields/DatePickerField/DatePickerField";
import AddIcon from "@mui/icons-material/Add";
import { ClientCompanySelect } from "pages/AnalyticsPage/MachineTranslationAnalyticsPage/components/ClientCompanySelect";
import { SelectOption } from "@mui/base";

dayjs.extend(utc);
dayjs.extend(timezone);

const breadcrumbs: BreadcrumbItem[] = [
  {
    path: "/analytics",
    label: "Analytics"
  },
  {
    path: "/analytics/lqaRatingAnalytics",
    label: "AQE Rating Analytics - Document Translation",
    active: true
  }
];

enum FieldNames {
  client = "client",
  from = "from",
  to = "to"
}

interface FormValues {
  [FieldNames.client]: SelectOption<string> | null;
  [FieldNames.from]: string;
  [FieldNames.to]: string;
}

const LqaRatingAnalyticsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [hasRunningReports, setHasRunningReports] = React.useState(false);

  const initialValues: FormValues = React.useMemo(() => {
    const now = new Date();
    const defaultFrom = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    return {
      client: null,
      from: dayjs(defaultFrom).format(DATE_PICKER_FORMAT),
      to: dayjs(now).format(DATE_PICKER_FORMAT)
    };
  }, []);

  const {
    mutate: createReport,
    isLoading: isCreating,
    error: createError,
    reset: resetCreateError
  } = useMutation(MetricsService.createLqaRatingAnalyticsReport, {
    onSuccess: () => {
      queryClient.invalidateQueries(["listLqaRatingReports"]);
      setDialogOpen(false);
    }
  });

  const handleCreateReportClick = React.useCallback(() => {
    resetCreateError();
    setDialogOpen(true);
  }, [resetCreateError]);

  const handleCloseDialog = React.useCallback(() => {
    if (!isCreating) {
      setDialogOpen(false);
    }
  }, [isCreating]);

  const handleRunningReportsChange = React.useCallback((hasRunning: boolean) => {
    setHasRunningReports(hasRunning);
  }, []);

  const validate = React.useCallback((values: FormValues) => {
    const fromValue = dayjs(values[FieldNames.from], DATE_PICKER_FORMAT);
    const toValue = dayjs(values[FieldNames.to], DATE_PICKER_FORMAT);
    const errors: Record<string, string> = {};
    if (!values[FieldNames.client]?.value) {
      errors[FieldNames.client] = "Client is required";
    }
    if (!values[FieldNames.from]) {
      errors[FieldNames.from] = "Start date is required";
    }
    if (!values[FieldNames.to]) {
      errors[FieldNames.to] = "End date is required";
    }
    if (toValue.isBefore(fromValue)) {
      errors[FieldNames.to] = "Start date must be before end date";
    } else if (toValue.subtract(365, "day").isAfter(fromValue)) {
      errors[FieldNames.to] = "Search range cannot exceed 365 days";
    }
    return errors;
  }, []);

  const onSubmit = React.useCallback(
    (values: FormValues) => {
      if (!values[FieldNames.client]?.value) {
        return;
      }
      const fromValue = dayjs
        .tz(values[FieldNames.from], DATE_PICKER_FORMAT, "America/Los_Angeles")
        .startOf("day")
        .toISOString();
      const toValue = dayjs
        .tz(values[FieldNames.to], DATE_PICKER_FORMAT, "America/Los_Angeles")
        .endOf("day")
        .toISOString();
      createReport({
        requestBody: {
          clientId: values[FieldNames.client].value,
          fromDate: fromValue,
          toDate: toValue
        }
      });
    },
    [createReport]
  );

  return (
    <PageLayout
      selectedNavMenu={NavMenuOption.ANALYTICS}
      title={<Typography variant="h6">AQE Rating Analytics - Document Translation</Typography>}
      breadcrumbs={breadcrumbs}
    >
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body1" color="text.secondary">
          Generate and view reports detailing the linguistic quality ratings for Document Translation. Reports are
          generated asynchronously and can be downloaded when complete.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateReportClick}
          disabled={isCreating || hasRunningReports}
          sx={{ ml: 2, whiteSpace: "nowrap" }}
        >
          {isCreating ? "Creating..." : hasRunningReports ? "Report Generation in Progress..." : "Generate New Report"}
        </Button>
      </Box>

      <LqaRatingReportsList onRunningReportsChange={handleRunningReportsChange} />
      {createError && <SnackbarApiError error={createError} />}

      {dialogOpen && (
        <Dialog open={true} onClose={handleCloseDialog} fullWidth={true} maxWidth="sm">
          <Formik initialValues={initialValues} validate={validate} onSubmit={onSubmit} validateOnMount={false}>
            {({ setFieldValue }) => (
              <Form noValidate={true} autoComplete="off" autoCorrect="off">
                <DialogTitle>Generate AQE Rating Analytics Report</DialogTitle>
                <DialogContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Select the client and date range for the report. The report will be generated asynchronously and may
                    take several minutes to complete.
                  </Typography>
                  <Stack spacing={3} sx={{ mt: 1 }}>
                    <ClientCompanySelect
                      name={FieldNames.client}
                      onChange={option => setFieldValue(FieldNames.client, option)}
                      variant="outlined"
                      size="small"
                    />
                    <DatePickerField
                      name={FieldNames.from}
                      label="Start date"
                      variant="outlined"
                      size="small"
                      disableFuture={true}
                    />
                    <DatePickerField
                      name={FieldNames.to}
                      label="End date"
                      variant="outlined"
                      size="small"
                      disableFuture={true}
                    />
                  </Stack>
                  {createError && (
                    <Box sx={{ mt: 2 }}>
                      <ApiErrorMessage apiError={createError} />
                    </Box>
                  )}
                </DialogContent>
                <DialogActions>
                  <LoadingButton variant="contained" isLoading={isCreating} color="primary" type="submit">
                    Generate Report
                  </LoadingButton>
                  <Button onClick={handleCloseDialog} disabled={isCreating}>
                    Cancel
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </Dialog>
      )}
    </PageLayout>
  );
};

export { LqaRatingAnalyticsPage };
